// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { State, ThresholdTypes } from "@utils/types";
import { UIMessages } from "../../config/constant";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIService } from "../../utils/apiService";
import { AppConfig } from "../../config/config";
import { enqueueSnackbarMessage } from "../commonSlice/common";
import { SnackMessage } from "../../config/constant";
import axios, { HttpStatusCode } from "axios";

interface CustomLocationMap {
  location: string;
  customMap: string[];
}

interface ThresholdPayload {
  key: ThresholdTypes;
  value: number;
}

interface AppConfigState {
  state: State;
  stateMessage: string | null;
  errorMessage: string | null;
  config: AppConfigInfo | null;
  [key: string]: any;
}

export interface AppConfigInfo {
  allCountries: string[];
  consultancyThreshold: number;
  salaryThreshold: number;
  consultancyRestrictedRoles: string[];
  reimbursementsAllowedCountries: string[];
  customLocationMap: CustomLocationMap[];
}

const initialState: AppConfigState = {
  state: State.idle,
  stateMessage: "",
  errorMessage: "",
  config: null,
};

export const fetchAppConfig = createAsyncThunk(
  "appConfig/fetchAppConfig",
  async (_, { dispatch, rejectWithValue }) => {
    APIService.getCancelToken().cancel();
    const newCancelTokenSource = APIService.updateCancelToken();
    return new Promise<AppConfigInfo>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.getAppConfig}`, {
          cancelToken: newCancelTokenSource.token,
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return rejectWithValue("Request canceled");
          }
          dispatch(
            enqueueSnackbarMessage({
              message:
                error.response?.status === HttpStatusCode.InternalServerError
                  ? SnackMessage.error.fetchAppConfigMessage
                  : String(error.response.data.message),
              type: "error",
            })
          );
          reject(error.response.data.message);
        });
    });
  }
);

export const updateThreshold = createAsyncThunk(
  "appConfig/updateThreshold",
  async (payload: ThresholdPayload,{ dispatch, rejectWithValue }) => {
    APIService.getCancelToken().cancel();
    const newCancelTokenSource = APIService.updateCancelToken();
    return new Promise<{key: ThresholdTypes, value: number}>((resolve, reject) => {
      APIService.getInstance()
        .patch(`${AppConfig.serviceUrls.updateThreshold}`,
          payload,
          {
            cancelToken: newCancelTokenSource.token,
          }
        )
        .then((res) => {
          resolve({
            key: payload.key,
            value: payload.value
          });
          dispatch(
            enqueueSnackbarMessage({
              message:
                res.status === HttpStatusCode.Ok
                  ? UIMessages.success.thresholdUpdate
                  : String(res.data.message),
              type: "success",
            })
          );
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return rejectWithValue("Request canceled");
          }
          dispatch(
            enqueueSnackbarMessage({
              message:
                error.response?.status === HttpStatusCode.InternalServerError
                  ? SnackMessage.error.fetchAppConfigMessage
                  : String(error.response.data.message),
              type: "error",
            })
          );
          reject(error.response.data.message);
        });
    });
  }
);

const AppConfigSlice = createSlice({
  name: "appConfig",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppConfig.pending, (state) => {
        state.state = State.loading;
        state.stateMessage = UIMessages.loading.loadingAppConfig;
      })
      .addCase(fetchAppConfig.fulfilled, (state, action: PayloadAction<AppConfigInfo>) => {
        state.state = State.success;
        state.stateMessage = UIMessages.success.appConfigFetch;
        state.config = action.payload;
      })
      .addCase(fetchAppConfig.rejected, (state, action) => {
        state.state = State.failed;
        state.stateMessage = UIMessages.error.appConfig;
        state.errorMessage = action.payload as string;
      })
      .addCase(updateThreshold.pending, (state) => {
        state.state = State.loading;
        state.stateMessage = UIMessages.loading.loadingAppConfig;
      })
      .addCase(updateThreshold.fulfilled, (state, action) => {
        state.state = State.success;
        state.stateMessage = UIMessages.success.thresholdUpdate;
        if(action.payload.key === ThresholdTypes.SALARY_THRESHOLD &&
          state.config
        ){
          state.config.salaryThreshold = action.payload.value;
        }
        if(action.payload.key === ThresholdTypes.CONSULTANCY_THRESHOLD &&
          state.config
        ){
          state.config.consultancyThreshold = action.payload.value;
        }
      })
      .addCase(updateThreshold.rejected, (state) => {
        state.state = State.failed;
        state.stateMessage = UIMessages.error.updateThreshold;
      });
  },
});

export default AppConfigSlice.reducer;